import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    AppWindow,
    Target,
    Eye,
    Gem,
    ShieldCheck,
    Settings as SettingsIcon,
    Users,
    TrendingUp,
    Building2,
    Database,
    Globe,
    Quote,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import {
    DOWNLOAD_HREF,
    TopBar,
    FooterBar,
    GradientOrbVisual,
} from "@/components/marketing/Chrome";

/**
 * About — company/mission page at /about. Stats in TrustedWorldwide are
 * shown as given in the reference the user provided and confirmed
 * ("use them as shown") rather than omitted or replaced -- see the
 * conversation where that was explicitly resolved before this page
 * was built.
 */
export default function About() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="about-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <Hero />
            <OurStory />
            <MissionVisionValues />
            <TrustedWorldwide />
            <FinalCTA />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function Hero() {
    return (
        <section className="relative z-10 overflow-hidden border-b border-white/5 px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="about-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[140px]" />
            <div className="relative mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
                <div>
                    <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">About</div>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                        Built for what <span className="text-[#3fb8ff]">moves you.</span>
                    </h1>
                    <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-white/75">
                        MoovBytes helps organizations move data forward by making data migration simple, reliable, and secure.
                    </p>
                    <p className="mt-3 max-w-lg text-[13.5px] leading-relaxed text-white/55">
                        We build powerful, easy-to-use Windows software for IT professionals tackling real-world migration challenges — from individual workstations to complex enterprise environments.
                    </p>
                    <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row">
                        <Link
                            to={DOWNLOAD_HREF}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-6 py-3.5 text-[14px] font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110 sm:w-auto"
                        >
                            <AppWindow size={16} />
                            Download for Windows
                        </Link>
                        <Link
                            to="/documentation"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-3.5 text-[14px] font-semibold text-white/85 transition hover:border-white/30 hover:bg-white/[0.06] hover:text-white sm:w-auto"
                        >
                            View Documentation
                        </Link>
                    </div>
                    <div className="mt-6 text-[12.5px] text-white/40">
                        Windows 10/11 &nbsp;•&nbsp; Windows Server 2019–2025 &nbsp;•&nbsp; 64-bit
                    </div>
                </div>
                <GradientOrbVisual
                    heading={<>DATA<br />ENVIRONMENTS<br />PEOPLE<br />FORWARD.</>}
                />
            </div>
        </section>
    );
}

function OurStory() {
    return (
        <section className="relative z-10 border-b border-white/5 px-6 py-20 sm:px-6" data-testid="about-story">
            <div className="mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Our Story</div>
                    <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">A focused mission. A bigger impact.</h2>
                    <p className="mt-4 text-[14px] leading-relaxed text-white/65">
                        MoovBytes was created by IT professionals who understand the real challenges of moving data. We saw a need for migration software that is powerful yet simple to use, delivering reliable results without unnecessary complexity.
                    </p>
                    <p className="mt-4 text-[14px] leading-relaxed text-white/65">
                        Today, organizations around the world trust MoovBytes to move what matters — user data, applications, and entire systems — with confidence.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <Quote size={26} className="text-[#3fb8ff]/60" />
                    <p className="mt-3 font-display text-lg font-medium leading-relaxed text-white/85">
                        "Our goal is simple: remove complexity from data migration so organizations can move forward with confidence."
                    </p>
                    <div className="mt-4 text-[12.5px] text-white/45">The MoovBytes Team</div>
                </div>
            </div>
        </section>
    );
}

function MissionVisionValues() {
    const VALUES = [
        { icon: ShieldCheck, title: "Reliability", body: "Deliver results you can trust." },
        { icon: SettingsIcon, title: "Simplicity", body: "Powerful tools, without the complexity." },
        { icon: Users, title: "Customer Focus", body: "Your success drives our development." },
        { icon: TrendingUp, title: "Continuous Improvement", body: "Always evolving to meet new challenges." },
    ];
    return (
        <section className="relative z-10 border-b border-white/5 px-6 py-20 sm:px-6" data-testid="about-mvv">
            <div className="mx-auto grid max-w-[1600px] gap-5 lg:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <Target size={22} className="text-[#3fb8ff]" />
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[#3fb8ff]">Our Mission</div>
                    <div className="mt-2 font-display text-xl font-bold leading-snug">To make data migration simple, reliable, and accessible for every organization.</div>
                    <p className="mt-3 text-[13px] leading-relaxed text-white/55">
                        We empower IT professionals with the tools they need to move data, reduce downtime, and keep their organizations running.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <Eye size={22} className="text-[#3fb8ff]" />
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[#3fb8ff]">Our Vision</div>
                    <div className="mt-2 font-display text-xl font-bold leading-snug">A world where moving data between systems is no longer a barrier to progress.</div>
                    <p className="mt-3 text-[13px] leading-relaxed text-white/55">
                        We envision a future where data migration is seamless, predictable, and a catalyst for business growth and innovation.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <Gem size={22} className="text-[#3fb8ff]" />
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[#3fb8ff]">Our Values</div>
                    <div className="mt-2 font-display text-xl font-bold leading-snug">The principles that move us forward.</div>
                    <ul className="mt-4 space-y-3">
                        {VALUES.map((v) => (
                            <li key={v.title} className="flex items-start gap-2.5">
                                <v.icon size={15} className="mt-0.5 shrink-0 text-[#3fb8ff]" />
                                <div>
                                    <div className="text-[12.5px] font-semibold text-white/85">{v.title}</div>
                                    <div className="text-[11.5px] text-white/50">{v.body}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function TrustedWorldwide() {
    const STATS = [
        { icon: Building2, value: "10K+", label: "Organizations worldwide" },
        { icon: Users, value: "250K+", label: "IT professionals trust MoovBytes" },
        { icon: Database, value: "500M+", label: "Millions of items migrated" },
        { icon: Globe, value: "100+", label: "Environments across the globe" },
    ];
    return (
        <section className="relative z-10 px-6 py-20 sm:px-6" data-testid="about-trusted-worldwide">
            <div className="mx-auto max-w-[1600px]">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Trusted Worldwide</div>
                        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Migration software for what's next.</h2>
                    </div>
                    <p className="max-w-md text-[13px] leading-relaxed text-white/55">
                        From small teams to global enterprises, MoovBytes is trusted by IT professionals to handle their most important migration projects.
                    </p>
                </div>
                <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
                    {STATS.map((s) => (
                        <div key={s.label}>
                            <s.icon size={22} className="text-[#3fb8ff]" />
                            <div className="mt-2 font-display text-2xl font-bold text-white">{s.value}</div>
                            <div className="mt-0.5 text-[12px] text-white/55">{s.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="relative z-10 px-6 pb-20 sm:px-6" data-testid="about-cta">
            <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#00C2FF]/10 via-white/[0.03] to-[#5b5ef0]/15 p-8 sm:flex-row sm:items-center">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Ready to move forward?</div>
                    <h2 className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">Join thousands of IT professionals who trust MoovBytes.</h2>
                    <p className="mt-1.5 text-[13px] text-white/60">Get started today and experience a better way to migrate.</p>
                </div>
                <div className="shrink-0 text-center">
                    <Link
                        to={DOWNLOAD_HREF}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110"
                    >
                        <AppWindow size={16} />
                        Download for Windows
                    </Link>
                    <div className="mt-2 text-[11px] text-white/40">Windows 10/11 · Windows Server 2019–2025 · 64-bit</div>
                </div>
            </div>
        </section>
    );
}
